import { Fragment, useCallback, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  BookTextIcon,
  Button,
  ExternalLinkIcon,
  IconButton,
  PlusIcon,
  Tooltip,
  toast,
} from '@buffer-mono/popcorn'
import { isRpcUpdate } from '~publish/legacy/post/helpers'
import { useFetchPostForComposer } from '~publish/hooks/postForComposer/useFetchPostForComposer'
import { channel } from '~publish/legacy/routes'

import { usePostDetailsDialog } from '~publish/components/PostDialogs'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import type { Service } from '../../graphqlEnums'
import { useTrackTakeawayCtaClicked } from '../../hooks/useTrackTakeawayCtaClicked'
import type { TakeawayCtaTrackingContext } from '../../hooks/useTrackTakeawayCtaClicked'
import { getCreatePostPrefill } from './getCreatePostPrefill'
import { getEngageAudienceRoute } from './getEngageAudienceRoute'
import { getMediaLearnMoreUrl } from './learnMoreUrl'
import { useTakeawayCtaOptions } from './TakeawaysContext'
import { toTitleCase } from './toTitleCase'
import { useRepostPostEligibility } from './useRepostPostEligibility'
import type { TakeawayItem, TakeawayItemCta } from './types'

import styles from './Takeaways.module.css'

// CTAs that open the composer — hidden for actors without posting access,
// for whom the composer would open with no channel selected.
const COMPOSER_CTAS: ReadonlySet<string> = new Set([
  'createPost',
  'publishDraft',
  'repostPost',
])

/**
 * The action buttons under a takeaway's description. Each `TakeawayCta` names an
 * action; this maps that action to what it opens, and drops any it cannot serve.
 */
export function TakeawayCtas({
  takeaway,
  ctaChannelId,
  channelService,
  tracking,
}: {
  takeaway: TakeawayItem
  /** The channel the actions act on, already resolved by the card. */
  ctaChannelId: string
  channelService?: Service | null
  tracking: TakeawayCtaTrackingContext
}): JSX.Element {
  const { canCreatePostsOn, onOpenRecommendedTimesDialog } =
    useTakeawayCtaOptions()
  const canCreatePosts = canCreatePostsOn(ctaChannelId)
  const navigate = useNavigate()
  const openGlobalModal = useOpenGlobalModal()
  const {
    createNewPostInComposer,
    editPostInComposer,
    duplicatePostInComposer,
  } = usePostComposer()
  const fetchPostForComposer = useFetchPostForComposer()

  const trackCta = useTrackTakeawayCtaClicked()

  const { prefillPostData, activeSidePanel, prefillAiPrompt } = useMemo(
    () =>
      getCreatePostPrefill(
        takeaway.metadata?.createPostType,
        channelService,
        takeaway.metadata?.prefillAiPrompt,
      ),
    [
      takeaway.metadata?.createPostType,
      takeaway.metadata?.prefillAiPrompt,
      channelService,
    ],
  )

  const repostPostId = takeaway.metadata?.postId ?? null
  const [repostLoading, setRepostLoading] = useState(false)
  const { openDetailsDialog } = usePostDetailsDialog({
    postId: repostPostId ?? '',
  })

  const isRepost = takeaway.category === 'repostPost'
  const {
    canDuplicate,
    canViewPost,
    externalLink,
    loading: eligibilityLoading,
  } = useRepostPostEligibility(isRepost ? repostPostId : null)
  // Native posts (not shared via Buffer) can't be duplicated and would error on
  // click, so we disable the Duplicate CTA for them. Stay optimistic while the
  // lookup is in flight so a valid CTA isn't needlessly delayed.
  const repostBlocked = isRepost && !eligibilityLoading && !canDuplicate

  const handleRepostPost = useCallback(
    (ctaLabel: string): void => {
      if (!ctaChannelId || !repostPostId) return
      trackCta.repostPost({ ...tracking, ctaLabel })
      setRepostLoading(true)
      void (async (): Promise<void> => {
        try {
          const post = await fetchPostForComposer(repostPostId)
          if (!isRpcUpdate(post)) {
            toast.error('Failed to load post for duplication')
            return
          }
          const {
            id: _id,
            due_at: _due,
            day: _day,
            scheduled_at: _sa,
            scheduledAt: _sat,
            serviceUpdateId: _suid,
            createdAt: _ca,
            statistics: _stats,
            status: _status,
            serviceLink: _sl,
            tags,
            ...rest
          } = post
          await duplicatePostInComposer({
            cta: 'publish-insights-takeaways-repostPost-1',
            channels: [ctaChannelId],
            prefillPostData: {
              ...rest,
              tags: tags?.map((tag: { id: string }) => tag.id),
              isSent: false,
              isPastDue: false,
              isDraft: false,
            },
            duplicatedFrom: repostPostId,
          })
        } catch (e) {
          const message = e instanceof Error ? e.message : 'Unknown error'
          toast.error(`Failed to duplicate post: ${message}`)
        } finally {
          setRepostLoading(false)
        }
      })()
    },
    [
      ctaChannelId,
      repostPostId,
      duplicatePostInComposer,
      fetchPostForComposer,
      trackCta,
      tracking,
    ],
  )

  const handleViewPostDetails = useCallback(
    (ctaLabel: string): void => {
      trackCta.viewPost({ ...tracking, ctaLabel })
      openDetailsDialog({ cta: 'publish-insights-takeaways-viewPost-1' })
    },
    [trackCta, tracking, openDetailsDialog],
  )

  const ctaHandlers = useMemo<
    Record<string, (ctaLabel: string) => void>
  >(() => {
    return {
      createPost: (ctaLabel: string): void => {
        trackCta.createPost({ ...tracking, ctaLabel })
        const composerOptions =
          activeSidePanel || prefillAiPrompt
            ? {
                ...(activeSidePanel && { activeSidePanel }),
                ...(prefillAiPrompt && { prefillAiPrompt }),
              }
            : undefined
        void createNewPostInComposer(
          {
            cta: 'publish-insights-takeaways-createPost-1',
            channels: [ctaChannelId],
            ...(prefillPostData && { prefillPostData }),
          },
          ...(composerOptions !== undefined ? [composerOptions] : []),
        )
      },
      publishDraft: (ctaLabel: string): void => {
        trackCta.publishDraft({ ...tracking, ctaLabel })
        if (takeaway.metadata?.draftId) {
          void editPostInComposer({
            cta: 'publish-insights-takeaways-publishDraft-1',
            postId: takeaway.metadata.draftId,
          })
        } else {
          navigate(channel.getRoute(ctaChannelId, { tab: 'drafts' }))
        }
      },
      repostPost: handleRepostPost,
      engageAudience: (ctaLabel: string): void => {
        trackCta.engageAudience({ ...tracking, ctaLabel })
        navigate(getEngageAudienceRoute(takeaway.channelIds ?? []))
      },
      adjustPostingGoal: (ctaLabel: string): void => {
        trackCta.adjustPostingGoal({ ...tracking, ctaLabel })
        openGlobalModal(MODALS.postingGoalConfiguration, {
          channelId: ctaChannelId,
        })
      },
      // Registered only with a dialog to open, so the `!ctaHandler` guard below
      // hides the CTA rather than rendering one that does nothing.
      ...(onOpenRecommendedTimesDialog && {
        adjustPostingSchedule: (ctaLabel: string): void => {
          trackCta.adjustPostingSchedule({ ...tracking, ctaLabel })
          onOpenRecommendedTimesDialog(ctaChannelId)
        },
      }),
      exploreTemplates: (ctaLabel: string): void => {
        trackCta.exploreTemplates({ ...tracking, ctaLabel })
        navigate(
          takeaway.category === 'engageAudience'
            ? '/create/templates?filters=Question'
            : '/create/templates',
        )
      },
    }
  }, [
    ctaChannelId,
    navigate,
    createNewPostInComposer,
    editPostInComposer,
    prefillPostData,
    activeSidePanel,
    prefillAiPrompt,
    takeaway.metadata?.draftId,
    takeaway.category,
    takeaway.channelIds,
    onOpenRecommendedTimesDialog,
    handleRepostPost,
    trackCta,
    tracking,
    openGlobalModal,
  ])

  const handler = ctaHandlers[takeaway.category]
  const isRenderableCta = (cta: TakeawayItemCta): boolean => {
    if (cta.url) return true

    const action = cta.action
    if (!action || !ctaHandlers[action]) return false
    if (canCreatePosts) return true

    if (action === 'repostPost') {
      return Boolean(canViewPost && externalLink)
    }

    return !COMPOSER_CTAS.has(action)
  }

  const renderableCtas = (takeaway.ctas ?? [])
    .map((cta) =>
      cta.action === 'learnMoreAboutFormat'
        ? Object.assign({}, cta, {
            url: getMediaLearnMoreUrl(channelService),
          })
        : cta,
    )
    .toSorted((a, b) => {
      if (a.variant === b.variant) return 0
      return a.variant === 'primary' ? -1 : 1
    })
    .filter(isRenderableCta)
  const iconCta =
    renderableCtas.length > 1
      ? (renderableCtas.findLast(
          (cta) =>
            cta.variant === 'secondary' &&
            (cta.action === 'exploreTemplates' || cta.action === 'createPost'),
        ) ??
        renderableCtas.find(
          (cta) => cta.variant === 'primary' && cta.action === 'createPost',
        ))
      : undefined
  const orderedCtas = iconCta
    ? [...renderableCtas.filter((cta) => cta !== iconCta), iconCta]
    : renderableCtas
  const structuredCtas =
    takeaway.ctas && takeaway.ctas.length > 0
      ? orderedCtas.map((cta) => ({ cta, isIcon: cta === iconCta }))
      : null

  return (
    <>
      {structuredCtas && (
        <div className={styles.ctaGroup}>
          {structuredCtas.map(({ cta, isIcon }) => {
            const ctaLabel =
              takeaway.category === 'engageAudience' &&
              cta.action === 'exploreTemplates'
                ? 'Browse Templates'
                : toTitleCase(cta.label)
            if (cta.url) {
              return (
                <Button
                  key={cta.url}
                  as="a"
                  href={cta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  size="small"
                  className={styles.ctaButton}
                  onClick={(): void =>
                    trackCta.learnMoreAboutFormat({
                      ...tracking,
                      ctaLabel,
                    })
                  }
                >
                  {ctaLabel}
                </Button>
              )
            }
            // Repost is unavailable — the post wasn't shared via Buffer,
            // or the actor can't create posts. When we have a link to the
            // original on the network, offer "View post" (the in-app
            // preview) plus an external link to the original.
            if (
              cta.action === 'repostPost' &&
              (repostBlocked || !canCreatePosts)
            ) {
              const blockedReason =
                'We can only duplicate posts shared via Buffer'
              if (canViewPost && externalLink) {
                return (
                  <Fragment key={cta.action}>
                    <Button
                      variant="secondary"
                      size="small"
                      className={styles.ctaButton}
                      onClick={(): void =>
                        handleViewPostDetails('View Post Details')
                      }
                    >
                      View Post Details
                    </Button>
                    <IconButton
                      as="a"
                      href={externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      label="View Post"
                      tooltip="View Post"
                      variant="secondary"
                      size="small"
                      className={styles.ctaButton}
                      onClick={(): void =>
                        trackCta.viewPostExternal({
                          ...tracking,
                          ctaLabel: 'View Post',
                        })
                      }
                    >
                      <ExternalLinkIcon />
                    </IconButton>
                  </Fragment>
                )
              }
              // The disabled fallback below explains non-Buffer posts,
              // not permissions — hide the CTA entirely.
              if (!canCreatePosts) {
                return null
              }
              // No link to offer: fall back to a disabled CTA that still
              // surfaces the reason through its accessible name (a disabled
              // control can't open a tooltip).
              return (
                <Tooltip key={cta.action} content={blockedReason}>
                  <span className={styles.disabledCtaWrapper}>
                    <Button
                      variant="secondary"
                      size="small"
                      className={styles.ctaButton}
                      disabled
                      aria-label={`${ctaLabel}. ${blockedReason}`}
                    >
                      {ctaLabel}
                    </Button>
                  </span>
                </Tooltip>
              )
            }
            const action = cta.action
            const ctaHandler = action ? ctaHandlers[action] : undefined
            if (!action || !ctaHandler) {
              return null
            }
            const icon = isIcon ? (
              action === 'exploreTemplates' ? (
                <BookTextIcon />
              ) : action === 'createPost' ? (
                <PlusIcon />
              ) : null
            ) : null
            if (icon) {
              return (
                <IconButton
                  key={action}
                  variant="secondary"
                  size="small"
                  className={styles.ctaButton}
                  label={ctaLabel}
                  tooltip={ctaLabel}
                  onClick={(): void => ctaHandler(ctaLabel)}
                >
                  {icon}
                </IconButton>
              )
            }
            return (
              <Button
                key={action}
                variant="secondary"
                size="small"
                className={styles.ctaButton}
                loading={action === 'repostPost' && repostLoading}
                onClick={(): void => ctaHandler(ctaLabel)}
              >
                {ctaLabel}
              </Button>
            )
          })}
        </div>
      )}
      {!structuredCtas && takeaway.ctaLabel && takeaway.ctaUrl && (
        <Button
          as="a"
          href={takeaway.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
          size="small"
          className={styles.ctaButton}
          onClick={takeaway.onCtaClick}
        >
          {toTitleCase(takeaway.ctaLabel)}
        </Button>
      )}
      {!structuredCtas &&
        takeaway.ctaLabel &&
        !takeaway.ctaUrl &&
        handler &&
        (canCreatePosts || !COMPOSER_CTAS.has(takeaway.category)) && (
          <Button
            variant="secondary"
            size="small"
            className={styles.ctaButton}
            onClick={(): void => handler(toTitleCase(takeaway.ctaLabel ?? ''))}
          >
            {toTitleCase(takeaway.ctaLabel)}
          </Button>
        )}
    </>
  )
}
